import * as React from "react";

const SIDEBAR_STORAGE_KEY = "admin:sidebar-open";

/**
 * Controls the admin sidebar's expanded/collapsed state and persists it to
 * localStorage so the choice survives page reloads.
 *
 * Returns a tuple of the current `open` value (seeded from the cache on first
 * render) and a setter to pass to `SidebarProvider`'s `onOpenChange`.
 *
 * @param defaultOpen - value to use when nothing is cached yet
 */
export function usePersistentSidebarState(defaultOpen = true) {
  const [open, setOpenState] = React.useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored === "true") return true;
      if (stored === "false") return false;
      return defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  const setOpen = React.useCallback((value: boolean) => {
    setOpenState(value);
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value));
    } catch {
      // Persisting is best-effort; a failed write must never break the toggle.
    }
  }, []);

  return [open, setOpen] as const;
}
